// ---------------------------------------------------------------------------
// The controls a host can still reach once the game has started.
//
// **The list is `SAFETY_SETTINGS` and it is not restated here.** That array in
// `packages/shared/src/constants.ts` is what `validateSettingsPatch` accepts
// mid-game, and this panel is rendered by mapping over it — so a control on
// screen that the server would refuse, or a key the server accepts with nothing
// to press, are both impossible. `CONTROLS` is typed `Record<SafetySetting, …>`,
// which means adding a key over there fails to compile here rather than quietly
// leaving a host without the switch.
//
// **It is not a second copy of the lobby's settings and must not grow into one.**
// Everything else in `GameSettings` is a rule of the game and is fixed when the
// game starts, because a room that can retune the clock mid-round can retune it
// in response to how the round is going. What is here is what a host reaches for
// when something is going wrong in the room — chat off, the room locked,
// permissions withdrawn — and the moment any of it is wanted is by definition
// not the lobby. So it is short, it is one press from wherever the host is, and
// nothing in it changes what anybody scores.
//
// The dismissal rules are the ones every scrim in this app now has, and they are
// the same ones for the same reasons: `pointerdown` rather than a release, so a
// gesture that leaves the panel survives; `swallowNextClick` on a scrim press,
// so the dismissal does not also press whatever the scrim was covering; and
// Escape.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { SAFETY_SETTINGS } from '@countdown/shared';
import type {
  ClientGameState,
  ClientMessage,
  GameSettings,
  SafetySetting,
} from '@countdown/shared';
import { Button, Segmented, Toggle } from '../../../components/primitives/index.js';
import { swallowNextClick } from '../../../lib/swallowNextClick.js';
import { SafetyPlayers } from './SafetyPlayers.js';
import {
  CHAT_HINT,
  CHAT_LABEL,
  CHAT_OPTIONS,
  DOOR_HINT,
  DOOR_LABEL,
  DOOR_OPTIONS,
  PERMISSIONS_HINT,
  PERMISSIONS_LABEL,
  SELF_SELECT_HINT,
  SELF_SELECT_LABEL,
  VISIBILITY_HINT,
  VISIBILITY_LABEL,
} from './copy.js';

/** A label, a line about the state it is in, and a control on the right. */
function ChoiceRow({
  label,
  hint,
  control,
}: {
  label: string;
  hint: string;
  control: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-h-11 items-center gap-3">
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-[14px] font-medium text-ink">{label}</span>
        <span className="text-[12px] text-ink-dim">{hint}</span>
      </span>
      <span className="ml-auto shrink-0">{control}</span>
    </div>
  );
}

type Render = (settings: GameSettings, patch: (change: Partial<GameSettings>) => void) => ReactNode;

/**
 * One control per key in `SAFETY_SETTINGS`.
 *
 * `satisfies` rather than a bare annotation, so a key added to the shared list
 * with no entry here is a type error at the point the list is read.
 */
const CONTROLS = {
  roomLock: (settings, patch) => (
    <ChoiceRow
      label={DOOR_LABEL}
      hint={DOOR_HINT[settings.roomLock ?? 'open']}
      control={
        <Segmented
          label={DOOR_LABEL}
          size="sm"
          value={settings.roomLock ?? 'open'}
          options={DOOR_OPTIONS}
          onChange={(next) => patch({ roomLock: next })}
        />
      }
    />
  ),
  chatMode: (settings, patch) => (
    <ChoiceRow
      label={CHAT_LABEL}
      hint={CHAT_HINT[settings.chatMode]}
      control={
        <Segmented
          label={CHAT_LABEL}
          size="sm"
          value={settings.chatMode}
          options={CHAT_OPTIONS}
          onChange={(next) => patch({ chatMode: next })}
        />
      }
    />
  ),
  playerPermissions: (settings, patch) => (
    <Toggle
      checked={settings.playerPermissions}
      onChange={(next) => patch({ playerPermissions: next })}
      label={PERMISSIONS_LABEL}
      description={PERMISSIONS_HINT}
    />
  ),
  allowTeamSelfSelect: (settings, patch) => (
    <Toggle
      checked={settings.allowTeamSelfSelect}
      onChange={(next) => patch({ allowTeamSelfSelect: next })}
      label={SELF_SELECT_LABEL}
      description={SELF_SELECT_HINT}
    />
  ),
  visibility: (settings, patch) => (
    <ChoiceRow
      label={VISIBILITY_LABEL}
      hint={VISIBILITY_HINT(settings.visibility === 'private')}
      control={
        <Segmented
          label={VISIBILITY_LABEL}
          size="sm"
          value={settings.visibility}
          options={[
            { value: 'public' as const, label: 'Public' },
            { value: 'private' as const, label: 'Private' },
          ]}
          onChange={(next) => patch({ visibility: next })}
        />
      }
    />
  ),
} satisfies Record<SafetySetting, Render>;

export function SafetyPanel({
  settings,
  state,
  send,
  onPatch,
  onClose,
}: {
  settings: GameSettings;
  /**
   * The room, for the Players tab. Optional so the lobby can mount this panel
   * without one: in the lobby the roster is already on screen behind it, with
   * the same four powers on every row, so a second way to reach them there is
   * clutter rather than cover. The tab appears where the roster does not.
   */
  state?: ClientGameState;
  send?: (message: ClientMessage) => void;
  onPatch: (change: Partial<GameSettings>) => void;
  onClose: () => void;
}): ReactElement {
  const [tab, setTab] = useState<'settings' | 'players'>('settings');
  const moderating = state !== undefined && send !== undefined;
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  return createPortal(
    <div
      data-testid="safety-scrim"
      className="fixed inset-0 z-[35] flex items-end justify-center p-4 sm:items-center"
      style={{ background: 'var(--c-scrim)' }}
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        swallowNextClick();
        close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-title"
        data-testid="safety-panel"
        className="sheet-up flex max-h-[80vh] w-full max-w-[420px] flex-col gap-2.5 overflow-y-auto overscroll-contain rounded-frame border border-line bg-surface p-4"
      >
        <h2 id="safety-title" className="text-xl font-bold">
          Room controls
        </h2>

        {/* TWO TABS, and the second one is not a convenience.
            Switching the chat off used to take kicking and banning with it,
            because every moderation control in the app hung off a row in the
            roster or a line in the chat — and both of those are gone mid-game
            with `chatMode: 'off'`. So the control a host reaches for when a room
            turns nasty was also the one that disarmed them. */}
        {moderating ? (
          <div role="tablist" aria-label="Room controls" className="flex gap-1">
            {(['settings', 'players'] as const).map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={tab === name}
                data-testid={`safety-tab-${name}`}
                onClick={() => setTab(name)}
                className={`min-h-11 flex-1 rounded-chip px-3 text-[13px] font-semibold transition-colors duration-150 ${
                  tab === name ? 'bg-raised text-ink' : 'text-ink-muted hover:bg-raised/60'
                }`}
              >
                {name === 'settings' ? 'Settings' : 'Players'}
              </button>
            ))}
          </div>
        ) : null}

        {tab === 'players' && moderating ? (
          <SafetyPlayers state={state} send={send} />
        ) : (
          <>
            {/* The one thing this panel has to say about itself, and it is a
                promise rather than a description: nothing in here is a rule of
                the game, so reaching for it mid-round cannot change a score. */}
            <p className="text-[13px] text-ink-muted">
              These can be changed while a game is running. None of them changes what anybody
              scores.
            </p>

            {SAFETY_SETTINGS.map((key) => (
              <div key={key} data-safety-setting={key} className="border-t border-line pt-2.5">
                {CONTROLS[key](settings, onPatch)}
              </div>
            ))}
          </>
        )}

        <Button variant="primary" onClick={close} block>
          Done
        </Button>
      </div>
    </div>,
    document.body,
  );
}
